import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock, Mail } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'
import { useLogin } from '@/hooks/useAuth'
import { getFieldErrors } from '@/lib/api'

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type FormData = z.infer<typeof schema>

export function LoginForm({ backgroundLocation }: { backgroundLocation?: unknown }) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string })?.from ?? '/'

  const login = useLogin()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  async function onSubmit(values: FormData) {
    try {
      await login.mutateAsync(values)
      navigate(from, { replace: true })
    } catch (err) {
      const fieldErrors = getFieldErrors(err)
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        setError(field as keyof FormData, { message: msg })
      })
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <Input
          label={t('email')}
          type="email"
          autoComplete="email"
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label={t('password')}
          type="password"
          autoComplete="current-password"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-end">
          <Link to="/auth/forgot-password" className="text-sm text-primary-600 hover:underline">
            {t('forgot_password')}
          </Link>
        </div>

        <Button type="submit" className="w-full" loading={login.isPending}>
          {t('login')}
        </Button>
      </form>

      <SocialLoginButtons />

      <p className="text-center text-sm text-gray-600">
        {t('no_account')}{' '}
        <Link
          to="/auth/register"
          state={backgroundLocation ? { backgroundLocation } : undefined}
          className="font-medium text-primary-600 hover:underline"
        >
          {t('sign_up')}
        </Link>
      </p>
    </div>
  )
}

